import React, { useEffect, useState } from 'react';
import { Cookie, X, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  getConsent,
  setConsent,
  OPEN_PREFERENCES_EVENT,
} from '../lib/cookieConsent';

/**
 * Lightweight first-party cookie consent banner.
 *
 *  - Shown automatically the first time a visitor has no stored consent.
 *  - Two buttons: "Accept all" / "Essential only". A small "Customize" link
 *    opens the preferences modal with an Essential (always on) + Marketing
 *    toggle, plus the "Do Not Sell or Share My Info" call-to-action.
 *  - The Footer's "Do Not Sell or Share My Info" link dispatches an event
 *    that opens the preferences modal directly even after the banner is
 *    dismissed.
 *  - The actual Meta Pixel injection lives in /lib/metaPixel.js and listens
 *    on the consent store — this component never touches the pixel directly.
 */
export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setMarketing(existing.marketing);
    }
    const onOpen = () => {
      const current = getConsent();
      setMarketing(current?.marketing ?? false);
      setPrefsOpen(true);
      setVisible(false);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, onOpen);
  }, []);

  const acceptAll = () => {
    setConsent({ marketing: true });
    setVisible(false);
    setPrefsOpen(false);
  };
  const essentialOnly = () => {
    setConsent({ marketing: false });
    setVisible(false);
    setPrefsOpen(false);
  };
  const savePreferences = () => {
    setConsent({ marketing });
    setPrefsOpen(false);
  };

  if (!visible && !prefsOpen) return null;

  return (
    <>
      {/* Sticky bottom banner — first-visit prompt */}
      {visible && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
          data-testid="cookie-banner"
          role="region"
          aria-label="Cookie consent"
        >
          <div className="pointer-events-auto mx-auto max-w-3xl bg-white shadow-xl rounded-2xl border border-gray-200 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-semibold text-gray-900">We use cookies</p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed">
                  Essential cookies make the site work. Marketing cookies (including the Meta Pixel) help us measure ad performance. You choose.{' '}
                  <a href="/cookie-policy" className="text-blue-600 hover:underline">Learn more</a>.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    onClick={acceptAll}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 px-4"
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </Button>
                  <Button
                    onClick={essentialOnly}
                    variant="outline"
                    className="text-sm h-9 px-4"
                    data-testid="cookie-essential-only"
                  >
                    Essential only
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setPrefsOpen(true); setVisible(false); }}
                    className="text-sm font-medium text-blue-600 hover:underline px-2 py-1"
                    data-testid="cookie-customize"
                  >
                    Customize
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal — opened from "Customize" or the footer "Do Not Sell" link */}
      {prefsOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-3 py-6"
          data-testid="cookie-preferences-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-preferences-title"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <h2 id="cookie-preferences-title" className="text-lg font-bold text-gray-900">
                  Cookie preferences
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setPrefsOpen(false)}
                className="text-gray-400 hover:text-gray-700 p-1"
                aria-label="Close cookie preferences"
                data-testid="cookie-prefs-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                Choose which cookies DriveHR may use. Essential cookies are always on. They keep the site functioning. Marketing cookies are optional.
              </p>

              {/* Essential row */}
              <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm">Essential</span>
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">Always on</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Required to sign you in, keep your session active, remember your preferences, and process payments securely.
                  </p>
                </div>
                <Switch checked disabled aria-label="Essential cookies (always on)" data-testid="cookie-switch-essential" />
              </div>

              {/* Marketing row */}
              <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm">Marketing</span>
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Meta Pixel</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Lets us measure which ads brought you here and show you more relevant ads on Facebook and Instagram. Off until you turn it on.
                  </p>
                </div>
                <Switch
                  checked={marketing}
                  onCheckedChange={setMarketing}
                  aria-label="Toggle marketing cookies"
                  data-testid="cookie-switch-marketing"
                />
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed">
                <strong>California residents (CCPA/CPRA):</strong> Turning Marketing off opts you out of the &quot;sale&quot; or &quot;sharing&quot; of your personal information with advertising partners such as Meta. You can change this anytime via the &quot;Do Not Sell or Share My Info&quot; link in the footer.
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2">
              <Button
                onClick={essentialOnly}
                variant="outline"
                className="text-sm h-9 px-4"
                data-testid="cookie-prefs-reject-all"
              >
                Reject all marketing
              </Button>
              <Button
                onClick={savePreferences}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 px-4"
                data-testid="cookie-prefs-save"
              >
                Save preferences
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
